// Owner/admin-only portfolio preview (EPD-10093).
//
// The Status "Preview" button opens this in a new tab. It renders the
// creator's current autosaved draft exactly as the public page would (the
// renderer's `public` surface + footer), whether or not the portfolio is
// published. It lives under the authenticated org area, loading the same data
// as the editor, so the public /p page can keep returning NOT_FOUND for
// drafts. No view is tracked here, so previews never count (EPD-10103).
//
// `toolkit_` opts out of nesting under the toolkit route, and the org layout
// renders it without the app chrome.

import { PortfolioPublicView } from "@/components/toolkit/portfolio-public-view";
import {
  canEditPortfolio,
  effectiveOrgRole,
  toolkitSearchSchema,
  useToolkitProfile,
} from "@/components/toolkit/use-toolkit-profile";
import {
  initialPortfolioTheme,
  readPortfolioPublished,
} from "@/lib/portfolio/theme";
import { useSocialStatsQuery } from "@/lib/queries/channel";
import { useDraftHighlightStatsQuery } from "@/lib/queries/public-profile";
import { Skeleton } from "@repo/ui";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Eye } from "lucide-react";
import { useEffect, useMemo, type ReactNode } from "react";

/**
 * Route guard: drafts are visible only to members who can edit the portfolio
 * (owner or admin — the roles `profiles.update` requires); any other member
 * gets the same not-found as a missing page.
 */
export function guardPortfolioPreview(
  context: Parameters<typeof effectiveOrgRole>[0],
) {
  if (!canEditPortfolio(effectiveOrgRole(context))) throw notFound();
}

export const Route = createFileRoute("/(org)/o/$orgSlug/toolkit_/preview")({
  beforeLoad: ({ context }) => guardPortfolioPreview(context),
  validateSearch: toolkitSearchSchema,
  component: PortfolioPreviewPage,
});

export function PortfolioPreviewPage() {
  const { orgSlug } = Route.useParams();
  const { profile: profileSlugParam } = Route.useSearch();

  const {
    organizationId,
    target,
    profileNotFound,
    isPending,
    previewData,
    previewPending,
  } = useToolkitProfile(orgSlug, profileSlugParam);

  const { data: socialStats } = useSocialStatsQuery(
    organizationId,
    target?.id ?? "",
  );
  // Only the profile id is needed, so highlight stats start alongside the
  // preview payload on their own (unbatched) request (EPD-12126).
  const { data: highlightPostStats } = useDraftHighlightStatsQuery(
    organizationId,
    target?.id ?? "",
    Boolean(target),
  );

  // Same initial theme the editor builds (incl. the seeded contact email for a
  // portfolio that has never been saved), so preview matches the editor.
  const theme = useMemo(
    () =>
      target ? initialPortfolioTheme(target.metadata, target.email) : null,
    [target],
  );
  const published = target ? readPortfolioPublished(target.metadata) : false;

  const profileName = previewData?.profile.name;
  useEffect(() => {
    if (!profileName) return;
    const previous = document.title;
    document.title = `Preview · ${profileName} · Portfolio`;
    return () => {
      document.title = previous;
    };
  }, [profileName]);

  let content: ReactNode;
  if (isPending || (target && previewPending)) {
    content = (
      <div className="mx-auto max-w-2xl space-y-6 px-6 py-12">
        <Skeleton className="h-24 w-24 rounded-full" />
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  } else if (profileNotFound) {
    content = (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <h1 className="text-xl font-semibold">Profile not found</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This organization has no profile with that link.
        </p>
      </div>
    );
  } else if (!target || !theme || !previewData) {
    content = (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <h1 className="text-xl font-semibold">Nothing to preview</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We couldn’t load this portfolio. Please try again.
        </p>
      </div>
    );
  } else {
    content = (
      <PortfolioPublicView
        data={previewData}
        theme={theme}
        socialStats={socialStats}
        highlightPostStats={highlightPostStats}
      />
    );
  }

  return (
    <div className="min-h-svh">
      <div
        role="status"
        className="sticky top-0 z-10 flex items-center justify-center gap-2 border-b bg-background/95 px-4 py-2 text-xs text-muted-foreground backdrop-blur"
      >
        <Eye className="h-3.5 w-3.5" />
        {published
          ? "Preview — this is how your live portfolio looks."
          : "Preview — your portfolio is a draft. Only you can see this."}
      </div>
      {content}
    </div>
  );
}
